import { Component, type ErrorInfo, type ReactNode } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Alert, IconButton, Collapse,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { APP_BASE } from '@/lib/api-base';
import { isChunkLoadError, reloadForNewVersion } from '@/lib/chunk-reload';
import { StaleVersionCard } from './StaleVersionCard';

type Props = { children: ReactNode };
type State = { error: Error | null; info: ErrorInfo | null; showDetails: boolean };

/**
 * Page-scoped error boundary.
 *
 * Wraps each route element in routes.tsx. When a render or lifecycle method
 * throws, this catches it and shows a recovery card instead of leaving the
 * shell blank. The user can retry the same route, go home, or expand to see
 * the stack trace (useful for bug reports).
 *
 * The error is also logged to console with a `[ErrorBoundary]` tag so it
 * shows up in browser-side observability tools that filter on prefix.
 *
 * Why a class component: React's error-boundary contract still requires
 * `componentDidCatch` + `getDerivedStateFromError`. There is no hook
 * equivalent yet (as of React 19).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null, showDetails: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
    this.setState({ info });
    // A page chunk that a deploy removed. lazyRoute normally reloads before
    // this point; this catches any import that did not go through it.
    if (isChunkLoadError(error)) reloadForNewVersion();
  }

  reset = () => {
    this.setState({ error: null, info: null, showDetails: false });
  };

  render() {
    if (!this.state.error) return this.props.children;

    const err = this.state.error;
    if (isChunkLoadError(err)) return <StaleVersionCard />;
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', mt: 4 }}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Something went wrong on this page
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                The page hit an unexpected error. Your data is safe — only the
                rendering failed. Try retrying; if it persists, send the
                details below to support.
              </Typography>
            </Box>

            <Alert severity="error" variant="outlined" sx={{ '& .MuiAlert-message': { wordBreak: 'break-word' } }}>
              <b>{err.name || 'Error'}:</b> {err.message || 'Unknown error'}
            </Alert>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button variant="contained" startIcon={<RefreshIcon />} onClick={this.reset}>
                Retry this page
              </Button>
              <Button variant="outlined" startIcon={<HomeIcon />}
                onClick={() => { this.reset(); window.location.href = `${APP_BASE}/`; }}>
                Go to dashboard
              </Button>
              <Box sx={{ flex: 1 }} />
              <IconButton size="small"
                onClick={() => this.setState({ showDetails: !this.state.showDetails })}
                aria-label="Toggle stack trace"
              >
                {this.state.showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Stack>

            <Collapse in={this.state.showDetails}>
              <Box sx={{
                mt: 1, p: 1.5,
                bgcolor: 'grey.50',
                border: 1, borderColor: 'divider', borderRadius: 0.5,
                fontFamily: 'ui-monospace, monospace', fontSize: 11,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                maxHeight: 360, overflow: 'auto',
              }}>
                <Box sx={{ fontWeight: 700, mb: 1 }}>{err.stack ?? '(no stack trace)'}</Box>
                {this.state.info?.componentStack && (
                  <>
                    <Box sx={{ fontWeight: 700, mt: 1.5, mb: 0.5, color: 'text.secondary' }}>
                      Component stack:
                    </Box>
                    <Box>{this.state.info.componentStack}</Box>
                  </>
                )}
              </Box>
            </Collapse>
          </Stack>
        </Paper>
      </Box>
    );
  }
}
